import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { graphql } from "@repo/graphql-types/gql";
import {
  createFileRoute,
  redirect,
  useLoaderData,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { parse } from "graphql";
import { useState, type JSX } from "react";
import { z } from "zod";

import {
  type RecurrenceScope,
  RecurrenceScopeChooser,
} from "@/components/recurring-appointment/recurrence-scope-chooser";
import { ensureQueryData } from "@/hooks/use-graphql";
import {
  capitalizeMonth,
  convertDateTimeIgnoringTimezone,
  formatIgnoringTimezone,
  getCurrentDate,
  removeSecondsFromTimeString,
} from "@/lib/helpers/date";
import { isConsultorioVirtual } from "@/lib/helpers/unit";
import { trackEvent } from "@/lib/tracking";

const ROUTE_ID = "/appointment/$appointmentId/patient-swap-recurrence";

const PatientSwapRecurrenceScopeQuery = graphql(/* GraphQL */ `
  query PatientSwapRecurrenceScopeQuery($codAgendamento: Int!) {
    agendamento: agendamentos_by_pk(codAgendamento: $codAgendamento) {
      codAgendamento
      codAgendamentoRecorrente
      data
      horaInicio
    }
  }
`);

interface RecurrenceUpcomingAppointmentsData {
  agendamentos: { codAgendamento: number; codUnidade: number }[];
}

interface RecurrenceUpcomingAppointmentsVariables {
  codAgendamentoRecorrente: number;
  data: string;
}

const RecurrenceUpcomingAppointmentsQuery = parse(/* GraphQL */ `
  query PatientSwapRecurrenceUpcomingQuery(
    $codAgendamentoRecorrente: Int!
    $data: date!
  ) {
    agendamentos(
      where: {
        codAgendamentoRecorrente: { _eq: $codAgendamentoRecorrente }
        cancelado: { _eq: false }
        data: { _gte: $data }
      }
    ) {
      codAgendamento
      codUnidade
    }
  }
`) as TypedDocumentNode<
  RecurrenceUpcomingAppointmentsData,
  RecurrenceUpcomingAppointmentsVariables
>;

const PatientSwapRecurrencePage = (): JSX.Element => {
  const { appointment, recurrenceCount, recurrenceQuotaCount } = useLoaderData({
    from: ROUTE_ID,
  });
  const router = useRouter();
  const navigate = useNavigate();

  const [scope, setScope] = useState<RecurrenceScope>("single");

  const dateObject = appointment.data
    ? convertDateTimeIgnoringTimezone(appointment.data)
    : null;
  const dayAndMonth = dateObject
    ? capitalizeMonth(formatIgnoringTimezone(dateObject, "d 'de' MMMM"))
    : "";
  const startTime = appointment.horaInicio
    ? removeSecondsFromTimeString(appointment.horaInicio)
    : "";

  const handleBack = (): void => {
    router.history.back();
  };

  const handleContinue = (): void => {
    trackEvent("Trocar paciente recorrência escopo selecionado", {
      agendamento: appointment.codAgendamento,
      escopo: scope,
    });

    void navigate({
      to: "/appointment/$appointmentId/patient-swap-search",
      params: { appointmentId: appointment.codAgendamento },
      search: { scope, count: recurrenceCount, quotaCount: recurrenceQuotaCount },
    });
  };

  return (
    <RecurrenceScopeChooser
      title="Trocar paciente"
      subtitle="Escolha se deseja trocar o paciente apenas deste agendamento ou de toda a recorrência"
      singleLabel="Apenas este agendamento"
      singleDescription={dayAndMonth ? `${dayAndMonth} · ${startTime}` : undefined}
      allLabel="Toda recorrência"
      allDescription={`Todos os ${recurrenceCount} agendamentos dessa recorrência`}
      value={scope}
      onChange={setScope}
      onContinue={handleContinue}
      onBack={handleBack}
    />
  );
};

export const Route = createFileRoute(
  "/appointment/$appointmentId/patient-swap-recurrence",
)({
  component: PatientSwapRecurrencePage,
  parseParams: (params) => ({
    appointmentId: z.int().parse(Number(params.appointmentId)),
  }),
  loader: async (opts) => {
    const data = await ensureQueryData(opts.context, PatientSwapRecurrenceScopeQuery, {
      codAgendamento: opts.params.appointmentId,
    });

    const appointment = data.agendamento;

    // Só faz sentido para agendamentos recorrentes; caso contrário volta à agenda.
    if (appointment?.codAgendamentoRecorrente == null) {
      throw redirect({ to: "/calendar" });
    }

    // A troca de todos vale para toda a recorrência (não só a partir da data
    // selecionada); contamos os agendamentos pendentes de hoje em diante.
    const recurrenceAppointments = await ensureQueryData(
      opts.context,
      RecurrenceUpcomingAppointmentsQuery,
      {
        codAgendamentoRecorrente: appointment.codAgendamentoRecorrente,
        data: formatIgnoringTimezone(getCurrentDate()),
      },
    );

    const upcomingAppointments = recurrenceAppointments.agendamentos;

    return {
      appointment,
      recurrenceCount: upcomingAppointments.length,
      recurrenceQuotaCount: upcomingAppointments.filter(
        (upcoming) => !isConsultorioVirtual(upcoming.codUnidade),
      ).length,
    };
  },
});
